import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faCompress,
  faExpand,
  faPlus,
  faPenToSquare,
  faTrash,
  faCirclePlus,
  faLevelUpAlt,
  faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import { AssetTableItem } from "../../../types/types";
import AssetTreeNode from "./AssetTreeNode";
import { TYPE_CONFIG } from "./typeConfig";
import { flattenAssets } from "./utils";

export interface AssetTreePanelProps {
  assets: AssetTableItem[];
  selectedId: number | null;
  expandedIds: Set<number>;
  filterText: string;
  onSelect: (_id: number) => void;
  onToggleExpand: (_id: number) => void;
  onFilterChange: (_text: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onAddRoot: () => void;
  onAddChild: (_parentId: number) => void;
  onDelete: (_id: number) => void;
  /** Open the import assets modal. */
  onImport?: () => void;
  // ── Selection mode ──────────────────────────────────────────────────────────
  selectionMode?: boolean;
  multiSelectedIds?: Set<number>;
  onToggleMultiSelect?: (_id: number) => void;
  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  draggedId?: number | null;
  dropTargetId?: number | null;
  invalidDropIds?: Set<number>;
  dropOverRoot?: boolean;
  onDragStart?: (_id: number, _e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDragOver?: (_id: number, _e: React.DragEvent) => void;
  onDragLeave?: (_id: number, _e: React.DragEvent) => void;
  onDrop?: (_targetId: number, _e: React.DragEvent) => void;
  onDropRoot?: () => void;
}

interface ContextMenuState {
  x: number;
  y: number;
  assetId: number;
}

const AssetTreePanel: React.FC<AssetTreePanelProps> = ({
  assets,
  selectedId,
  expandedIds,
  filterText,
  onSelect,
  onToggleExpand,
  onFilterChange,
  onExpandAll,
  onCollapseAll,
  onAddRoot,
  onAddChild,
  onDelete,
  onImport,
  selectionMode = false,
  multiSelectedIds = new Set(),
  onToggleMultiSelect,
  draggedId = null,
  dropTargetId = null,
  invalidDropIds = new Set(),
  dropOverRoot = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onDropRoot,
}) => {
  const navigate = useNavigate();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const ctxRef = useRef<HTMLDivElement>(null);
  const [typeFilter, setTypeFilter] = useState("");

  // Collect which types are actually present in the tree
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    flattenAssets(assets).forEach((a) => types.add(a.type));
    return types;
  }, [assets]);

  // Only show type buttons for types that exist AND have a config entry
  const typeButtons = useMemo(
    () => Object.entries(TYPE_CONFIG).filter(([type]) => availableTypes.has(type)),
    [availableTypes]
  );

  // Close context menu on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleContextMenu = (e: React.MouseEvent, assetId: number) => {
    e.preventDefault();
    const x = Math.min(e.clientX, window.innerWidth - 176);
    const y = Math.min(e.clientY, window.innerHeight - 200);
    setContextMenu({ x, y, assetId });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-zinc-800 flex-shrink-0">
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-2.5 top-2 text-zinc-500 text-xs pointer-events-none"
          />
          <input
            type="text"
            value={filterText}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Filter assets…"
            className="w-full bg-zinc-800 border border-zinc-700 rounded pl-7 pr-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
          />
        </div>
      </div>

      {/* Type filter buttons */}
      {typeButtons.length > 0 && (
        <div className="px-3 py-2 flex flex-wrap gap-1 border-b border-zinc-800/60 flex-shrink-0">
          <button
            onClick={() => setTypeFilter("")}
            title="All types"
            className={`px-2 py-0.5 text-[10px] rounded font-medium tracking-wide border transition-colors ${
              typeFilter === ""
                ? "bg-amber-400/20 text-amber-300 border-amber-400/40"
                : "text-zinc-500 border-zinc-700 hover:text-zinc-300 hover:border-zinc-500"
            }`}
          >
            All
          </button>
          {typeButtons.map(([type, cfg]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(typeFilter === type ? "" : type)}
              title={cfg.label}
              className={`w-7 h-7 flex items-center justify-center rounded border transition-colors ${
                typeFilter === type
                  ? `${cfg.bg} ${cfg.color} ${cfg.border}`
                  : "text-zinc-600 border-transparent hover:text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              <FontAwesomeIcon icon={cfg.icon} className="text-xs" />
            </button>
          ))}
        </div>
      )}

      {/* Controls row */}
      <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-800/60 flex-shrink-0">
        <span className="text-xs text-zinc-500 font-semibold uppercase tracking-widest">
          {typeFilter ? (TYPE_CONFIG[typeFilter]?.label ?? "Entries") : "Entries"}
        </span>
        <div className="flex gap-1">
          <button
            onClick={onCollapseAll}
            title="Collapse all"
            className="w-6 h-6 flex items-center justify-center text-zinc-600 hover:text-zinc-300 rounded transition-colors"
          >
            <FontAwesomeIcon icon={faCompress} className="text-xs" />
          </button>
          <button
            onClick={onExpandAll}
            title="Expand all"
            className="w-6 h-6 flex items-center justify-center text-zinc-600 hover:text-zinc-300 rounded transition-colors"
          >
            <FontAwesomeIcon icon={faExpand} className="text-xs" />
          </button>
        </div>
      </div>

      {/* Tree */}
      <div role="tree" className="flex-1 overflow-y-auto p-2 text-sm scrollbar-modern">
        {assets.length === 0 ? (
          <div className="text-center py-8 text-zinc-600 text-xs">
            No assets yet. Create your first asset below.
          </div>
        ) : (
          assets.map((asset) => (
            <AssetTreeNode
              key={asset.id}
              asset={asset}
              depth={0}
              selectedId={selectedId}
              expandedIds={expandedIds}
              filterText={filterText}
              typeFilter={typeFilter}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onContextMenu={handleContextMenu}
              selectionMode={selectionMode}
              multiSelectedIds={multiSelectedIds}
              onToggleMultiSelect={onToggleMultiSelect}
              draggedId={draggedId}
              dropTargetId={dropTargetId}
              invalidDropIds={invalidDropIds}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            />
          ))
        )}
      </div>

      {/* Add root footer */}
      <div className="p-2 border-t border-zinc-800/60 flex-shrink-0">
        {/* Root drop zone — only visible while a drag is active */}
        {draggedId !== null && (
          <div
            className={`mb-2 flex items-center justify-center gap-1.5 py-2 text-xs border border-dashed rounded transition-colors ${
              dropOverRoot
                ? "border-amber-400 bg-amber-400/10 text-amber-400"
                : "border-zinc-600 text-zinc-500"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              onDragOver?.(-1, e); // -1 signals "root zone" to the view
            }}
            onDragLeave={() => onDragLeave?.(-1, {} as React.DragEvent)}
            onDrop={(e) => {
              e.preventDefault();
              onDropRoot?.();
            }}
          >
            <FontAwesomeIcon icon={faLevelUpAlt} className="text-xs" />
            Drop here to make root
          </div>
        )}
        <button
          onClick={onAddRoot}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
        >
          <FontAwesomeIcon icon={faPlus} className="text-xs" />
          Add root entry
        </button>
        {onImport && (
          <button
            onClick={onImport}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
          >
            <FontAwesomeIcon icon={faFileImport} className="text-xs" />
            Import entries
          </button>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          ref={ctxRef}
          style={{ position: "fixed", left: contextMenu.x, top: contextMenu.y, zIndex: 100 }}
          className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl py-1 w-44 text-sm"
        >
          <button
            className="w-full text-left px-4 py-2 hover:bg-zinc-700 flex items-center gap-3 text-white transition-colors"
            onClick={() => {
              navigate(`/protected/assets/${contextMenu.assetId}`);
              setContextMenu(null);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} className="text-zinc-400 text-xs w-4" />
            Edit
          </button>
          <button
            className="w-full text-left px-4 py-2 hover:bg-zinc-700 flex items-center gap-3 text-white transition-colors"
            onClick={() => {
              onAddChild(contextMenu.assetId);
              setContextMenu(null);
            }}
          >
            <FontAwesomeIcon icon={faCirclePlus} className="text-zinc-400 text-xs w-4" />
            Add Child
          </button>
          <div className="border-t border-zinc-700 my-1" />
          <button
            className="w-full text-left px-4 py-2 hover:bg-zinc-700 flex items-center gap-3 text-red-400 transition-colors"
            onClick={() => {
              onDelete(contextMenu.assetId);
              setContextMenu(null);
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="text-xs w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default AssetTreePanel;
